import { chain } from 'icepick';
import isNil from 'lodash/isNil';
import type { Action, StoreActionApi } from '@atlassian/react-sweet-state';
import { ISSUE_LINKS_FIELDTYPE, type Field } from '../../../../common/constants/fields';
import type { State, ColumnName, MappingState } from '../../../../common/types';
import { getNewValidationState } from '../../../../common/utils';

export type SetFieldForColumnAction = (
	columnName: ColumnName,
	field: Field | null,
) => Action<State, void, void>;

const getNewMappedJiraFieldIds = (
	mappingState: MappingState,
	columnName: ColumnName,
	newField: Field | null,
) => {
	const newMappedJiraFieldIds = mappingState.mappedJiraFieldIds;
	const existingFieldId = mappingState.columns[columnName].field?.fieldId;
	const newFieldId = newField?.fieldId;

	if (!isNil(existingFieldId)) {
		newMappedJiraFieldIds.delete(existingFieldId);
	}

	if (!isNil(newFieldId)) {
		newMappedJiraFieldIds.add(newFieldId);
	}

	return newMappedJiraFieldIds;
};

const getMappedLinkFieldsCount = (
	mappingState: MappingState,
	columnName: ColumnName,
	newField: Field | null,
) => {
	const existingFieldId = mappingState.columns[columnName].field;
	let { mappedLinkFieldsCount } = mappingState;

	if (existingFieldId?.fieldType === ISSUE_LINKS_FIELDTYPE) {
		mappedLinkFieldsCount--;
	}

	if (newField?.fieldType === ISSUE_LINKS_FIELDTYPE) {
		mappedLinkFieldsCount++;
	}

	return mappedLinkFieldsCount;
};

export const setFieldForColumn =
	(columnName: ColumnName, field: Field | null) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const state = getState();

		if (state.mappingState === null) {
			return;
		}

		const mappedLinkFieldsCount = getMappedLinkFieldsCount(state.mappingState, columnName, field);

		const newState = chain(state)
			.setIn(['mappingState', 'columns', columnName, 'field'], field)
			.setIn(
				['mappingState', 'mappedJiraFieldIds'],
				getNewMappedJiraFieldIds(state.mappingState, columnName, field),
			)
			.setIn(
				['mappingState', 'validationError'],
				getNewValidationState(
					state.mappingState,
					columnName,
					mappedLinkFieldsCount,
					field?.fieldId,
				),
			)
			.setIn(['mappingState', 'mappedLinkFieldsCount'], mappedLinkFieldsCount)
			.setIn(['mappingState', 'userMapping'], null) // reset userMapping if field is changed
			.value();

		setState(newState);
	};
